import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useState, useEffect } from 'react';
import AlertLayer, { useAlertFilters, ALERT_TYPES, SEVERITY_LEVELS } from './AlertLayer';
import AvalancheLayer from './AvalancheLayer';
import { getMinSeverity } from '../utils/dataUtils.js';
import { REGIONS } from '../shared/regionData'; // ✅ import diretto, zero fetch

// ✅ Disponibile immediatamente da regionData.js, che importa regionList.json
const VALID_NAMES = new Set(Object.keys(REGIONS));

// Mappa esplicita: nome nel GeoJSON italy-regions.json → nome canonico di regionList.json
const GEOJSON_TO_CANONICAL = {
    "Abruzzo": "abruzzo",
    "Basilicata": "basilicata",
    "Calabria": "calabria",
    "Campania": "campania",
    "Emilia_Romagna": "emilia_romagna",
    "Friuli_Venezia_Giulia": "friuli_venezia_giulia",
    "Lazio": "lazio",
    "Liguria": "liguria",
    "Lombardia": "lombardia",
    "Marche": "marche",
    "Molise": "molise",
    "Piemonte": "piemonte",
    "Puglia": "puglia",
    "Sardegna": "sardegna",
    "Sicilia": "sicilia",
    "Toscana": "toscana",
    "Trentino_Alto_Adige": "trentino_alto_adige",
    "Umbria": "umbria",
    "Valle_di_Aosta": "valle_di_aosta",
    "Veneto": "veneto",
};



function toCanonical(geoJsonName) {
    const canonical = GEOJSON_TO_CANONICAL[geoJsonName];
    if (!canonical) {
        console.warn(`⚠️ RegionMap: nome GeoJSON non mappato: "${geoJsonName}"`);
        return null;
    }
    if (!VALID_NAMES.has(canonical)) {
        console.warn(`⚠️ RegionMap: "${canonical}" non è in regionList.json`);
        return null;
    }
    return canonical;
}

const RegionMap = ({ onRegionClick }) => {
    const [highlighted, setHighlighted] = useState(null);
    const [italyRegions, setItalyRegions] = useState(null);
    const alertControls = useAlertFilters();
    const [showAlertPanel, setShowAlertPanel] = useState(false);

    useEffect(() => {
        fetch(`/data/italy-regions.json`)
            .then(r => r.json())
            .then(data => setItalyRegions(data))
            .catch(err => console.error('Errore caricamento regioni:', err));
    }, []);

    const onEachRegion = (feature, layer) => {
        const geoName = feature.properties.reg_name || feature.properties.name;
        layer.setStyle({
            color: 'white',
            weight: 1,
            fillOpacity: 0.7,
            fillColor: getColor(geoName),
        });

        layer.on({
            mouseover: () => layer.setStyle({ weight: 2, fillOpacity: 1 }),
            mouseout: () => { if (highlighted !== geoName) layer.setStyle({ weight: 1, fillOpacity: 0.7 }); },
            click: () => {
                const canonical = toCanonical(geoName);
                if (!canonical) return;
                setHighlighted(geoName);
                if (onRegionClick) onRegionClick(canonical);
            }
        });
    };

    const getColor = (name) => {
        if (!italyRegions) return "#999999";
        const colors = [
            "#e41a1c", "#377eb8", "#4daf4a", "#984ea3", "#ff7f00",
            "#ffff33", "#a65628", "#f781bf", "#999999", "#66c2a5",
            "#fc8d62", "#8da0cb", "#e78ac3", "#a6d854", "#ffd92f",
            "#e5c494", "#b3b3b3", "#1b9e77", "#d95f02", "#7570b3"
        ];
        const idx = [...new Set(italyRegions.features.map(f => f.properties.reg_name))].indexOf(name);
        return colors[idx % colors.length];
    };

    console.log('ALERT ENABLED', alertControls.typeFilters, alertControls.severityFilters);

    return (
        <div style={{ position: 'relative', height: '90vh', width: '100%' }}>
            <MapContainer
                center={[42.5, 12.5]}
                zoom={6}
                minZoom={5}
                maxBounds={[[36.0, 6.5], [47.5, 18.8]]}
                maxBoundsViscosity={1.0}
                style={{ height: "100%", width: "100%" }}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                    bounds={[[36.0, 6.5], [47.5, 18.8]]}
                />
                {italyRegions && <GeoJSON data={italyRegions} onEachFeature={onEachRegion} />}
                <AlertLayer
                    enabled={alertControls.enabled}
                    typeFilters={alertControls.typeFilters}
                    severityFilters={alertControls.severityFilters}
                    showBadge={true}
                />
                <AvalancheLayer
                    minDangerLevel={getMinSeverity(alertControls)}
                    showTooltips={true}
                />
            </MapContainer>

            <button
                onClick={() => setShowAlertPanel(!showAlertPanel)}
                style={{
                    position: 'absolute', bottom: '20px', right: '20px',
                    width: '56px', height: '56px', borderRadius: '50%',
                    background: alertControls.enabled ? '#FF9800' : '#ddd',
                    color: 'white', border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    cursor: 'pointer', fontSize: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, transition: 'all 0.3s'
                }}
                title={showAlertPanel ? 'Chiudi pannello alert' : 'Apri pannello alert'}
            >
                ⚠️
            </button>

            {showAlertPanel && (
                <div style={{
                    position: 'absolute', bottom: '90px', right: '20px',
                    background: 'white', padding: '20px', borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)', zIndex: 1000,
                    maxWidth: '320px', maxHeight: 'calc(90vh - 120px)', overflowY: 'auto'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>⚠️ Alert Italia</h3>
                        <button onClick={() => setShowAlertPanel(false)}
                            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '4px' }}>
                            ✕
                        </button>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={alertControls.enabled}
                            onChange={(e) => alertControls.setEnabled(e.target.checked)}
                            style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }} />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Mostra alert sulla mappa</span>
                    </label>

                    {alertControls.enabled && (
                        <>
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>Tipologia</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {Object.entries(ALERT_TYPES).map(([type, info]) => (
                                        <button key={type} onClick={() => alertControls.toggleType(type)}
                                            style={{
                                                padding: '6px 12px', fontSize: '12px', border: 'none', borderRadius: '14px', cursor: 'pointer',
                                                background: alertControls.typeFilters.includes(type) ? info.color : '#f5f5f5',
                                                color: alertControls.typeFilters.includes(type) ? 'white' : '#666',
                                                transition: 'all 0.2s', fontWeight: '500'
                                            }}>
                                            {info.emoji} {info.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>Livello Allerta</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {Object.entries(SEVERITY_LEVELS).map(([sev, info]) => (
                                        <button key={sev} onClick={() => alertControls.toggleSeverity(sev)}
                                            style={{
                                                padding: '6px 12px', fontSize: '12px', border: 'none', borderRadius: '14px', cursor: 'pointer',
                                                background: alertControls.severityFilters.includes(sev) ? info.color : '#f5f5f5',
                                                color: alertControls.severityFilters.includes(sev) ? 'white' : '#666',
                                                transition: 'all 0.2s', fontWeight: '500'
                                            }}>
                                            {info.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button onClick={alertControls.reset}
                                style={{ width: '100%', padding: '10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '8px', background: 'white', cursor: 'pointer', color: '#666', fontWeight: '500', transition: 'background 0.2s' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                                Reset Filtri
                            </button>

                            <div style={{ marginTop: '16px', padding: '12px', background: '#e3f2fd', borderRadius: '8px', fontSize: '12px', color: '#1976d2', lineHeight: '1.5' }}>
                                💡 <strong>Suggerimento:</strong> Click su una regione per vedere i percorsi e gli alert locali dettagliati
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RegionMap;
